/**
 * GoogleSignInButton — "Continue with Google" full-page navigation button.
 *
 * Design: project/designs/39b-google-signin-ui.md
 *
 * Implemented as an <a> element (not a <button>) so the browser performs a
 * full-page navigation to the backend OAuth initiation endpoint. A <button>
 * with window.location.href would also work, but an anchor preserves browser
 * affordances (right-click, middle-click) and is semantically correct for
 * navigation actions.
 *
 * AC-3: full-page navigation (not a fetch)
 * AC-8: keyboard-accessible, visible focus ring, aria-hidden on logo SVG
 * AC-9: no Google client_secret; no Google client_id; no external SDK
 */

import React from 'react';

interface GoogleSignInButtonProps {
  /** URL-encoded relative path to redirect to after successful Google login. */
  returnTo: string;
}

/**
 * Google "G" logo SVG.
 * Colors per Google's brand guidelines (Sign in with Google button brand guide).
 * aria-hidden — the button's text label is the accessible name.
 */
function GoogleLogo() {
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 18 18"
      width="18"
      height="18"
      fill="none"
    >
      {/* Blue */}
      <path
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        fill="#4285F4"
      />
      {/* Green */}
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
        fill="#34A853"
      />
      {/* Yellow */}
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      {/* Red */}
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  );
}

export function GoogleSignInButton({ returnTo }: GoogleSignInButtonProps) {
  const href = `/api/v1/auth/google?return=${encodeURIComponent(returnTo)}`;

  return (
    <a
      href={href}
      className="flex items-center justify-center gap-3 w-full border border-gray-300 bg-white rounded-lg py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:shadow-sm active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 motion-safe:transition-colors"
    >
      <GoogleLogo />
      Continue with Google
    </a>
  );
}
